import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { BdaApplication } from "../models/BdaApplication";
import { RootState } from "../store";
import { AccessLevel, UsedItem } from "../models/generics";
import { Datasource } from "../models/Datasource";
import { WorkflowMode } from "../models/Workflow";
import { EnvVariable } from "../models/Environment";

// TODO improve types
interface DesignerState {
  application: BdaApplication;
  usedItems: UsedItem<any>[];
  availableStorages: Datasource[] | null;
  defaultStorage: number | undefined;
  environmentVariables: EnvVariable[] | undefined;
}

const initialState: DesignerState = {
  application: { ...new BdaApplication() },
  usedItems: [],
  availableStorages: null,
  defaultStorage: undefined,
  environmentVariables: undefined,
};

export const designerSlice = createSlice({
  name: 'designer',
  initialState,
  reducers: {
    // Redux Toolkit allows us to write "mutating" logic in reducers. It
    // doesn't actually mutate the state because it uses the Immer library,
    // which detects changes to a "draft state" and produces a brand new
    // immutable state based off those changes
    setApplication(state, action: PayloadAction<BdaApplication>) {
      state.application = action.payload;
    },
    changeMode(state, action: PayloadAction<WorkflowMode>) {
      state.application.workflows[0].mode = action.payload;
    },
    setApplicationAccessLevel(state, action: PayloadAction<AccessLevel>) {
      state.application.accessLevel = action.payload;
    },
    setUsedItems(state, action: PayloadAction<UsedItem<any>[]>) {
      state.usedItems = action.payload;
    },
    pushUsedItem(state, action: PayloadAction<UsedItem<any>>) {
      const newItem = action.payload;
      const oldItems = state.usedItems.map(item => item.ref);
      if (oldItems.includes(newItem.ref)) return;

      const newItems = [...state.usedItems];
      newItems.push(newItem);
      state.usedItems = newItems;
    },
    updateUsedItem(state, action: PayloadAction<UsedItem<any>>) {
      const inspectingItem = action.payload;
      const newItems = [...state.usedItems];

      if (inspectingItem.isNodePort) {
        const index = state.usedItems.findIndex(item => item.ref === inspectingItem.service!.ref);

        if (inspectingItem.isOutput) newItems[index].outputs = inspectingItem.service!.outputs;
        else newItems[index].inputs = inspectingItem.service!.inputs;
      }
      else {
        const index = state.usedItems.findIndex(item => item.ref === inspectingItem.ref);
        newItems[index] = inspectingItem;
      }

      state.usedItems = newItems;
    },
    setAvailableStorages(state, action: PayloadAction<Datasource[]>) {
      const storages = action.payload;
      state.availableStorages = storages;
    },
    setDefaultStorage(state, action: PayloadAction<number | Datasource | undefined>) {
      const storage = action.payload;
      state.defaultStorage = typeof storage === 'object' ? storage?.id! : storage;
    },
    setEnvironmentVariables(state, action: PayloadAction<EnvVariable[]>) {
      const variables = action.payload;
      state.environmentVariables = variables;
    },
    resetState(state) {
      state.application = { ...new BdaApplication() };
      state.availableStorages = null;
      state.defaultStorage = undefined;
      state.environmentVariables = undefined;
      state.usedItems = [];
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setApplication,
  updateUsedItem,
  pushUsedItem,
  changeMode, // TODO remove function and mode from application model
  setApplicationAccessLevel,
  setUsedItems,
  setAvailableStorages,
  setDefaultStorage,
  setEnvironmentVariables,
  resetState,
} = designerSlice.actions;

export const applicationServicesSelector = (state: RootState) => state.designer.application.workflows[0].services;
export const applicationAccessLevel = (state: RootState) => state.designer.application.accessLevel;
export const getUsedItemSelector = (state: RootState) => (id: string | null) => state.designer.usedItems.find(item => item.ref === id);
export const getEnvironmentVariableSelector = (state: RootState) => (id: string | undefined) => state.designer.environmentVariables?.find(env => id && env.id === +id);

export default designerSlice.reducer;